import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { getStudentAttendanceTrend } from "../../lib/api";

// Small term-over-term attendance trend for a single student's file.
export function AttendanceTrendChart({ studentId }) {
  const [terms, setTerms] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setTerms(null);
    setError(false);
    getStudentAttendanceTrend(studentId)
      .then((r) => { if (active) setTerms((r.data.terms || []).filter((t) => t.rate != null)); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [studentId]);

  const latest = terms && terms.length ? terms[terms.length - 1] : null;
  const prev = terms && terms.length > 1 ? terms[terms.length - 2] : null;
  const delta = latest && prev ? Math.round((latest.rate - prev.rate) * 10) / 10 : null;

  return (
    <Card data-testid="attendance-trend-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Attendance Trend</CardTitle>
        <CardDescription>Attendance rate term over term</CardDescription>
      </CardHeader>
      <CardContent>
        {terms === null && (
          <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground" data-testid="attendance-trend-loading">Loading trend…</div>
        )}
        {terms !== null && error && (
          <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">Couldn&apos;t load attendance trend.</div>
        )}
        {terms !== null && !error && terms.length === 0 && (
          <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground" data-testid="attendance-trend-empty">No term attendance recorded yet for this student.</div>
        )}
        {terms !== null && !error && terms.length > 0 && (
          <>
            {delta != null && (
              <p className="text-sm mb-2" data-testid="attendance-trend-delta">
                <span className="font-semibold text-[#00305E]">{latest.rate}%</span> this term
                <span className={`ml-2 font-medium ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                  {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"} {Math.abs(delta)} pts vs previous
                </span>
              </p>
            )}
            <ResponsiveContainer width="100%" height={190}>
              <LineChart data={terms} margin={{ top: 18, right: 34, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} width={40} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v, _n, p) => [`${v}%  (${p.payload.present} present / ${p.payload.total_expected} days)`, "Attendance"]}
                  labelFormatter={(l) => terms.find((t) => t.label === l)?.name + (terms.find((t) => t.label === l)?.academic_year ? ` · ${terms.find((t) => t.label === l).academic_year}` : "")}
                />
                <Line type="monotone" dataKey="rate" name="Attendance" stroke="#00305E" strokeWidth={2.5} dot={{ r: 4, fill: "#00305E" }} activeDot={{ r: 6 }}>
                  <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: "#00305E", fontWeight: 600 }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}
